import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, MessageCircle, MapPin } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

interface Mentee {
  id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  location: string | null;
  profession: string | null;
  completedSessions: number;
}

interface MyMenteesSectionProps {
  className?: string;
}

const MyMenteesSection = ({ className }: MyMenteesSectionProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [mentees, setMentees] = useState<Mentee[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchMyMentees();
    }
  }, [user]);

  const fetchMyMentees = async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      
      // Get completed sessions where the current user is the mentor
      // and there are appointment reviews (mentee left a review)
      const { data: completedSessions, error } = await supabase
        .from('session_bookings')
        .select(`
          mentee_id,
          id
        `)
        .eq('mentor_id', user.id)
        .eq('status', 'completed');

      if (error) {
        console.error("Error fetching completed sessions:", error);
        throw error;
      }

      if (!completedSessions || completedSessions.length === 0) {
        setMentees([]);
        return;
      }

      // Get booking IDs to check for reviews
      const bookingIds = completedSessions.map(session => session.id);
      
      const { data: reviews, error: reviewsError } = await supabase
        .from('appointment_reviews')
        .select('booking_id, reviewer_id')
        .in('booking_id', bookingIds);

      if (reviewsError) {
        console.error("Error fetching reviews:", reviewsError);
        throw reviewsError;
      }

      // Filter sessions that have reviews from mentees
      const sessionsWithReviews = completedSessions.filter(session => 
        reviews?.some(review => 
          review.booking_id === session.id && 
          review.reviewer_id === session.mentee_id
        )
      );

      // Get unique mentee IDs from sessions with reviews
      const uniqueMenteeIds = [...new Set(sessionsWithReviews.map(session => session.mentee_id))];
      
      if (uniqueMenteeIds.length === 0) {
        setMentees([]);
        return;
      }

      // Fetch mentee profiles separately
      const { data: menteeProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, avatar_url, location, profession')
        .in('id', uniqueMenteeIds);

      if (profilesError) {
        console.error("Error fetching mentee profiles:", profilesError);
        throw profilesError;
      }

      // Group by mentee and count sessions
      const menteeGroups = sessionsWithReviews.reduce((acc, session) => {
        const menteeId = session.mentee_id;
        const menteeProfile = menteeProfiles?.find(p => p.id === menteeId);
        
        if (!acc[menteeId] && menteeProfile) {
          acc[menteeId] = {
            id: menteeProfile.id,
            first_name: menteeProfile.first_name,
            last_name: menteeProfile.last_name,
            avatar_url: menteeProfile.avatar_url,
            location: menteeProfile.location,
            profession: menteeProfile.profession,
            completedSessions: 0
          };
        }
        if (acc[menteeId]) {
          acc[menteeId].completedSessions++;
        }
        return acc;
      }, {} as Record<string, Mentee>);

      const menteesList = Object.values(menteeGroups);
      setMentees(menteesList);
    } catch (error) {
      console.error("Error in fetchMyMentees:", error);
      toast({
        title: "Error",
        description: "Failed to load mentees data.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewProfile = (menteeId: string) => {
    navigate(`/mentee-profile/${menteeId}`);
  };

  const handleMessage = (menteeId: string) => {
    navigate(`/messages/${menteeId}`);
  };

  const getDisplayName = (mentee: Mentee) => {
    const fullName = `${mentee.first_name} ${mentee.last_name}`.trim();
    return fullName || "Unknown User";
  };

  if (isLoading) {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${className || ''}`}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center gap-4 mb-4">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div>
                <Skeleton className="h-5 w-32 mb-2" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
            <div className="space-y-2 mb-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (mentees.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
        <p className="text-gray-600 mb-2">You don't have any mentees yet.</p>
        <p className="text-sm text-gray-500">
          When mentees complete sessions and provide reviews, they will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${className || ''}`}>
      {mentees.map((mentee) => (
        <div key={mentee.id} className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
          <div className="p-6">
            <div className="flex items-start gap-4">
              <Avatar className="w-16 h-16 border-2 border-emu-blue">
                <AvatarImage src={mentee.avatar_url || ''} alt={getDisplayName(mentee)} />
                <AvatarFallback>{mentee.first_name?.charAt(0) || 'U'}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-semibold">{getDisplayName(mentee)}</h3>
                {mentee.profession && (
                  <p className="text-sm text-gray-600 mb-1">{mentee.profession}</p>
                )}
                {mentee.location && (
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin size={14} className="mr-1" />
                    {mentee.location}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  {mentee.completedSessions} completed session{mentee.completedSessions !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 border-t p-4">
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleViewProfile(mentee.id)}
                className="text-emu-blue border-emu-blue hover:bg-emu-blue/10"
              >
                <User size={14} className="mr-1" /> View Profile
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleMessage(mentee.id)}
                className="text-emu-blue border-emu-blue hover:bg-emu-blue/10"
              >
                <MessageCircle size={14} className="mr-1" /> Message
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MyMenteesSection;